import { format } from 'date-fns';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import staffMemberServices, {
  CreateContractProps,
  UpdateContractProps,
} from '@/services/staffMember';
import { Contract } from '@/types/api/staffMember';
import StaffMemberContractFormComponent, { FormData } from './StaffMemberContractForm.component';

type Props = {
  staffMemberId: string;
  contract?: Contract;
  onUpdateStaffMember?: () => void;
};

const StaffMemberContractForm = ({
  contract,
  onUpdateStaffMember,
  staffMemberId,
}: Props): JSX.Element => {
  const toast = useToastContext();

  const createContractMutation = useApiSubmit<Contract, CreateContractProps>(
    staffMemberServices.createContract
  );

  const updateContractMutation = useApiSubmit<Contract, UpdateContractProps>(
    staffMemberServices.updateContract
  );

  const handleSubmit = async ({ hireDate, ...data }: FormData): Promise<void> => {
    const formattedDate = format(hireDate, 'yyyy-MM-dd');

    if (contract) {
      try {
        toast.setMessage('Saving information...');
        toast.setSeverity('info');
        toast.showToast();

        await updateContractMutation.mutateAsync({
          id: contract.id,
          staffMemberId: contract.staffMemberId,
          hireDate: formattedDate,
          ...data,
        });

        toast.setMessage('The contract information has been updated.');
        toast.setSeverity('success');
        toast.showToast();
        onUpdateStaffMember?.();
      } catch {
        toast.setMessage('There was an error saving the data.');
        toast.setSeverity('error');
        toast.showToast();
      }
    } else {
      try {
        toast.setMessage('Saving information...');
        toast.setSeverity('info');
        toast.showToast();

        await createContractMutation.mutateAsync({
          staffMemberId,
          hireDate: formattedDate,
          ...data,
        });

        toast.setMessage('The contract information has been saved.');
        toast.setSeverity('success');
        toast.showToast();
        onUpdateStaffMember?.();
      } catch {
        toast.setMessage('There was an error saving the data.');
        toast.setSeverity('error');
        toast.showToast();
      }
    }
  };

  return (
    <StaffMemberContractFormComponent
      hireDate={contract?.hireDate ? new Date(contract.hireDate) : undefined}
      type={contract?.type}
      status={contract?.status}
      onSubmit={handleSubmit}
    />
  );
};

export default StaffMemberContractForm;
